import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BudgetHeader } from './BudgetHeader';
import { FundraisingChart } from './FundraisingChart';
import { TopStrategies } from './TopStrategies';
import { KeyIssues } from './KeyIssues';
import { Flag, Calendar, Users, MapPin } from 'lucide-react';
import { format, isValid } from 'date-fns';
import { getBudgetPrediction } from '../../lib/gemini';
import { LoadingScreen } from '../LoadingScreen';
import type { CampaignPhase, CampaignStatus } from '../../lib/types';
import { useCampaignStore } from '../../lib/store/campaignStore';

export function Dashboard() {
  const campaignData = useCampaignStore(state => state.campaignData);
  const [isLoading, setIsLoading] = useState(false);
  const [budgetPrediction, setBudgetPrediction] = useState<any>(null);
  
  const [campaignPhase, setCampaignPhase] = useState<CampaignPhase>(() => {
    const electionType = campaignData?.electionType || '';
    if (electionType.includes('primary')) return 'primary';
    if (electionType.includes('general')) return 'general';
    return 'primary';
  });
  
  const [campaignStatus, setCampaignStatus] = useState<CampaignStatus>('ahead');
  const firstName = campaignData?.candidateInfo?.name?.split(' ')?.[0] || 'Candidate';

  const handleStatusChange = async (status: CampaignStatus) => {
    setIsLoading(true);
    setCampaignStatus(status);

    try {
      const prediction = await getBudgetPrediction({
        office: campaignData?.office || '',
        location: campaignData?.location || { state: 'Unknown' },
        electionType: campaignData?.electionType || 'general-even',
        electionDate: campaignData?.electionDate || new Date(),
        phase: campaignPhase,
        status,
        incumbentStatus: campaignData?.incumbentStatus || 'challenger'
      });

      setBudgetPrediction(prediction);
    } catch (error) {
      console.error('Failed to update budget prediction:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePredictionChange = (prediction: any) => {
    setBudgetPrediction(prediction);
  };

  const formatElectionType = (type: string) => {
    switch (type) {
      case 'primary':
        return 'Primary Election';
      case 'general-even':
        return 'General Election (Even Year)';
      case 'general-odd':
        return 'General Election (Odd Year)';
      case 'special':
        return 'Special Election';
      case 'runoff':
        return 'Runoff Election';
      default:
        return 'Election';
    }
  };

  const getFormattedElectionDate = () => {
    let date = campaignData?.electionDate;
    
    if (typeof date === 'string') {
      date = new Date(date);
    }

    if (date && isValid(date)) {
      return format(date, 'MMMM d, yyyy');
    }
    
    return format(new Date(), 'MMMM d, yyyy');
  };

  return (
    <div className="min-h-screen bg-[#0a1229] text-white">
      {isLoading && <LoadingScreen />}
      
      <header className="bg-[#1a2236] border-b border-white/10">
        <div className="container mx-auto px-4">
          <div className="py-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6 mb-3">
              <motion.div 
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6"
              >
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold mb-1">Welcome, {firstName}</h1>
                  <p className="text-sm text-gray-400">Campaign Manager Dashboard</p>
                </div>
                <div className="hidden sm:block h-12 w-px bg-white/10" />
                <div>
                  <div className="text-xl sm:text-2xl font-bold text-[#026fb9] break-words">{campaignData?.office}</div>
                  <div className="flex items-center gap-2 text-gray-400">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{campaignData?.location?.city || campaignData?.location?.county || campaignData?.location?.state}</span>
                  </div>
                </div>
              </motion.div>
            </div>

            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 py-2 px-3 bg-white/5 rounded-lg text-sm overflow-x-auto whitespace-nowrap"
            >
              <div className="flex items-center gap-2 text-gray-400">
                <Flag className="w-4 h-4 flex-shrink-0" />
                <span>Election Type:</span>
                <span className="text-white">
                  {formatElectionType(campaignData?.electionType)}
                </span>
              </div>

              <div className="hidden sm:block w-px h-4 bg-white/10" />

              <div className="flex items-center gap-2 text-gray-400">
                <Calendar className="w-4 h-4 flex-shrink-0" />
                <span>Election Date:</span>
                <span className="text-white">
                  {getFormattedElectionDate()}
                </span>
              </div>

              <div className="hidden sm:block w-px h-4 bg-white/10" />

              <div className="flex items-center gap-2 text-gray-400">
                <Users className="w-4 h-4 flex-shrink-0" />
                <span>Status:</span>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleStatusChange('ahead')}
                    className={`px-2 py-0.5 rounded ${
                      campaignStatus === 'ahead' 
                        ? 'bg-green-600 text-white' 
                        : 'hover:bg-white/10'
                    }`}
                  >
                    Leading
                  </button>
                  <button
                    onClick={() => handleStatusChange('behind')}
                    className={`px-2 py-0.5 rounded ${
                      campaignStatus === 'behind' 
                        ? 'bg-red-600 text-white' 
                        : 'hover:bg-white/10'
                    }`}
                  >
                    Trailing
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 sm:py-8">
        <div className="mb-6 sm:mb-8">
          <BudgetHeader
            phase={campaignPhase}
            status={campaignStatus}
            electionDate={campaignData?.electionDate || new Date()}
            electionType={campaignData?.electionType || 'general-even'}
            office={campaignData?.office || ''}
            location={campaignData?.location || { state: 'Unknown' }}
            incumbentStatus={campaignData?.incumbentStatus || 'challenger'}
            prediction={budgetPrediction}
            onPredictionChange={handlePredictionChange}
          />
        </div>

        <div className="bg-[#1a2236] rounded-xl p-4 sm:p-6 mb-6 sm:mb-8">
          <FundraisingChart
            phase={campaignPhase}
            status={campaignStatus}
            electionDate={campaignData?.electionDate || new Date()}
            office={campaignData?.office || ''}
            prediction={budgetPrediction}
          />
        </div>

        <div className="space-y-6 sm:space-y-8">
          <div className="w-full">
            <KeyIssues />
          </div>
        </div>

        <div className="mt-6 sm:mt-8">
          <TopStrategies
            phase={campaignPhase}
            status={campaignStatus}
            office={campaignData?.office || ''}
            location={campaignData?.location?.city || campaignData?.location?.county || campaignData?.location?.state}
          />
        </div>
      </div>
    </div>
  );
}